// Мост между Razor-компонентами и браузерным API.
// Всё, что компоненту нужно от страницы (reCAPTCHA, cookie), объявляется здесь
// и вызывается через IJSRuntime. Отдельные компоненты собственный JS не тянут.

// Пространство собирают несколько файлов, порядок подключения не фиксирован,
// поэтому каждый дополняет уже существующий объект.
window.zs = window.zs || ({} as Zs);

window.zs.recaptcha = {
    // Возвращает токен reCAPTCHA v3 для указанного действия.
    // null означает, что скрипт не загрузился или ключ не задан на странице —
    // без токена сервер форму не принимает.
    token: function (action: string): Promise<string | null> {
        // Скрипт Google подключается лениво (zs.captcha в script.core):
        // execute() сам дожидается его загрузки и grecaptcha.ready.
        // Отказ загрузчика (блокировщик, офлайн) — это null, а не исключение
        // в цепи Blazor.
        if (window.zs.captcha && typeof window.zs.captcha.execute === "function") {
            try {
                return window.zs.captcha.execute(action).then(
                    function (token) { return token || null; },
                    function () { return null; });
            } catch {
                return Promise.resolve(null);
            }
        }

        return new Promise<string | null>(function (resolve) {
            const grecaptcha = window.grecaptcha;
            const key = window.recaptchaPublicKey;

            if (!grecaptcha || !key) {
                resolve(null);
                return;
            }

            try {
                grecaptcha.ready(function () {
                    grecaptcha
                        .execute(key, { action: action })
                        .then(resolve, function () { resolve(null); });
                });
            } catch {
                resolve(null);
            }
        });
    }
};

window.zs.history = {
    // Меняет адрес без перезагрузки: выдачей поиска можно поделиться ссылкой,
    // но саму страницу компонент перерисовывает сам.
    replace: function (url: string): void {
        try {
            window.history.replaceState(null, "", url);
        } catch {
            /* Браузер запретил менять историю — не критично. */
        }
    }
};

// --- Пометки «статья оценена» ------------------------------------------------
// Раньше на каждую оценённую статью ставилась своя cookie, и заголовок Cookie
// рос с каждой оценкой. Теперь значения лежат в localStorage под одним ключом,
// а cookie одна на всех — JSON-словарь (её читает сервер, чтобы не предлагать
// оценку повторно). Словарь ограничен последними записями.
(function () {
    const storageKey = "zs.ratings";
    const cookieName = "zs_ratings";
    const maxEntries = 50;
    const cookieMaxAge = 60 * 60 * 24 * 365;

    type Entries = { [name: string]: string };

    function readStorage(): Entries | null {
        try {
            const raw = window.localStorage.getItem(storageKey);
            return raw ? (JSON.parse(raw) as Entries) : {};
        } catch {
            // Хранилище недоступно (приватный режим, запрет на сайт).
            return null;
        }
    }

    function readCookie(): Entries {
        const prefix = cookieName + "=";
        const parts = document.cookie ? document.cookie.split(";") : [];

        for (const raw of parts) {
            const part = raw.trim();
            if (part.indexOf(prefix) === 0) {
                try {
                    return JSON.parse(decodeURIComponent(part.substring(prefix.length))) as Entries;
                } catch {
                    return {};
                }
            }
        }

        return {};
    }

    function trim(entries: Entries): Entries {
        const keys = Object.keys(entries);
        if (keys.length <= maxEntries) {
            return entries;
        }

        // Порядок ключей в объекте — порядок вставки: отбрасываем самые старые.
        const res: Entries = {};
        keys.slice(keys.length - maxEntries).forEach(function (key) { res[key] = entries[key]; });
        return res;
    }

    function writeCookie(entries: Entries): void {
        const secure = window.location.protocol === "https:" ? ";Secure" : "";
        document.cookie = cookieName + "=" + encodeURIComponent(JSON.stringify(entries))
            + ";path=/;max-age=" + cookieMaxAge + ";SameSite=Lax" + secure;
    }

    window.zs.cookie = {
        set: function (name: string, value: string): void {
            const stored = readStorage();
            const entries = trim(Object.assign({}, stored || readCookie(), { [name]: value }));

            if (stored !== null) {
                try {
                    window.localStorage.setItem(storageKey, JSON.stringify(entries));
                } catch {
                    /* Квота исчерпана — остаётся cookie. */
                }
            }

            writeCookie(entries);
        },

        get: function (name: string): string | null {
            const stored = readStorage();
            const entries = stored || readCookie();
            const value = entries[name];

            if (value !== undefined) {
                return value;
            }

            // В localStorage пусто, но cookie могла прийти с другой вкладки/сессии.
            return stored !== null ? (readCookie()[name] ?? null) : null;
        }
    };
})();

// --- Кнопки блока обрыва соединения -----------------------------------------
// Разметка — #components-reconnect-modal в _Scripts.cshtml. blazor.server.js
// лишь переключает на блоке классы состояния, а что делать по нажатию, не
// знает, поэтому повторяем поведение его стандартного окна: при успехе
// Blazor сам переведёт блок в hide, при неудаче состояние ставим мы.
(function (dialog: HTMLElement | null) {
    if (!dialog) {
        return;
    }

    const stateClasses = ["show", "retrying", "failed", "rejected", "paused", "resume-failed", "hide"]
        .map(function (state) { return "components-reconnect-" + state; });

    function setState(state: "show" | "failed" | "resume-failed"): void {
        dialog!.classList.remove(...stateClasses);
        dialog!.classList.add("components-reconnect-" + state);
    }

    function reload(): void {
        window.location.reload();
    }

    async function retry(): Promise<void> {
        const blazor = window.Blazor;
        if (!blazor) {
            reload();
            return;
        }

        setState("show");
        try {
            if (await blazor.reconnect() || await blazor.resumeCircuit()) {
                return;
            }
            // Сервер не помнит цепь и восстановить её не из чего — только заново.
            reload();
        } catch {
            setState("failed");
        }
    }

    async function resume(): Promise<void> {
        const blazor = window.Blazor;
        if (!blazor) {
            reload();
            return;
        }

        setState("show");
        try {
            if (await blazor.resumeCircuit()) {
                return;
            }
            reload();
        } catch {
            setState("resume-failed");
        }
    }

    // Blazor не снимает класс components-reconnect-retrying на время самой
    // попытки, когда счётчик уже дошёл до нуля. Отсчёт показываем только
    // пока он идёт, остальное время — обычное «восстанавливаем соединение».
    dialog.addEventListener("components-reconnect-state-changed", function (e: Event) {
        const detail = (e as CustomEvent<{ state: string; secondsToNextAttempt?: number }>).detail;
        const countdown = detail.state === "retrying" && (detail.secondsToNextAttempt || 0) > 0;
        dialog!.classList.toggle("reconnect-countdown", countdown);
    });

    dialog.querySelector("[data-reconnect-retry]")?.addEventListener("click", retry);
    dialog.querySelector("[data-reconnect-resume]")?.addEventListener("click", resume);
    dialog.querySelector("[data-reconnect-reload]")?.addEventListener("click", reload);
})(document.getElementById("components-reconnect-modal"));
